import React from "react"
import {
  DragDropContext,
  Draggable,
  DraggableStateSnapshot,
  DraggingStyle,
  Droppable,
  DropResult,
  NotDraggingStyle,
} from "react-beautiful-dnd"
import { Icon } from "@cd2/components"
import { icons } from "../constants/icons"

interface IDraggableListProps<T> {
  items: T[]

  onOrderChange(newItems: T[]): void

  keyExtractor(item: T): string

  renderItem(
    item: T,
    DragHandle: (props: { children?: React.ReactNode }) => JSX.Element,
    isDragging: boolean,
  ): React.ReactNode
}

export function DraggableList<T = any>({
  items,
  onOrderChange,
  renderItem,
  keyExtractor,
}: IDraggableListProps<T>) {
  function getStyle(
    style: DraggingStyle | NotDraggingStyle | undefined,
    snapshot: DraggableStateSnapshot,
  ) {
    if (!snapshot.isDropAnimating) {
      return style
    }
    return {
      ...style,
      // cannot be 0, but make it super tiny
      transitionDuration: `0.001s`,
    }
  }

  function reorder<T>(list: T[], startIndex: number, endIndex: number): T[] {
    const result = Array.from(list)
    const [removed] = result.splice(startIndex, 1)
    result.splice(endIndex, 0, removed)

    return result
  }

  function handleDragEnd(result: DropResult) {
    // dropped outside the list
    if (!result.destination) {
      return
    }

    const newItems = reorder(items, result.source.index, result.destination.index)
    onOrderChange(newItems)
  }

  const defaultDragHandleChildren = <Icon icon={icons.grip} />

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <Droppable droppableId="droppable">
        {provided => (
          <div {...provided.droppableProps} ref={provided.innerRef}>
            {items.map((item, index) => (
              <Draggable key={keyExtractor(item)} draggableId={keyExtractor(item)} index={index}>
                {(provided, snapshot) => {
                  const DragHandle = ({ children }: { children?: React.ReactNode }) => (
                    <div {...provided.dragHandleProps} tabIndex={undefined}>
                      {children || defaultDragHandleChildren}
                    </div>
                  )
                  return (
                    <div
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      style={getStyle(provided.draggableProps.style, snapshot)}
                    >
                      {renderItem(item, DragHandle, snapshot.isDragging)}
                    </div>
                  )
                }}
              </Draggable>
            ))}

            {provided.placeholder}
          </div>
        )}
      </Droppable>
    </DragDropContext>
  )
}
